import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  PDKAddIcon,
  PDKExtendedIcon,
  PDKLocalDeliveryIcon,
  PDKLongHaulIcon,
  PDKPickupIcon,
} from '@/components/ui/pdk-icons';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { HOTKEYS } from '@/lib/hotkeys';
import type { Deal } from '@/models/registry';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import DealDeliveryPreferenceBadge from './deal-delivery-preference-badge';
import useUpdateDeliveryPreferenceMutation from './hooks/use-update-delivery-preference-mutation';

export const DELIVERY_PREFERENCE_OPTIONS_MAP: Option<
  Deal['delivery_preference']
>[] = [
  {
    value: 'none',
    label: 'None',
    icon: <PDKAddIcon className="h-3.5 w-3.5" />,
    hotkey: '0',
    abbreviation: 'None',
  },
  {
    value: 'pickup',
    label: 'Pickup',
    icon: <PDKPickupIcon className="h-3.5 w-3.5" />,
    hotkey: '1',
    abbreviation: 'Pickup',
  },
  {
    value: 'local_delivery',
    label: 'Local Delivery',
    icon: <PDKLocalDeliveryIcon className="h-3.5 w-3.5" />,
    hotkey: '2',
    abbreviation: 'Local',
  },
  {
    value: 'extended',
    label: 'Extended',
    icon: <PDKExtendedIcon className="h-3.5 w-3.5" />,
    hotkey: '3',
    abbreviation: 'Ext',
  },
  {
    value: 'delivery',
    label: 'Long Haul',
    icon: <PDKLongHaulIcon className="h-3.5 w-3.5" />,
    hotkey: '4',
    abbreviation: 'Long',
  },
] as const;

export type DealDeliveryPreferenceSelectorProps = {
  dealId: string;
  delivery_preference: Deal['delivery_preference'];
  side?: 'left' | 'right' | 'bottom' | 'top';
  align?: 'center' | 'start' | 'end';
  hideCopy?: boolean;
  withHotkeyListener?: boolean;
  useAbbreviation?: boolean;
  className?: string;
  hideIcon?: boolean;
};

const DealDeliveryPreferenceSelector = ({
  dealId,
  delivery_preference,
  side = 'left',
  align = 'start',
  hideCopy = false,
  withHotkeyListener = false,
  useAbbreviation = false,
  className,
  hideIcon = false,
}: DealDeliveryPreferenceSelectorProps) => {
  const { updateDeliveryPreferenceMutation } =
    useUpdateDeliveryPreferenceMutation({ dealId });

  const handleDeliveryPreferenceChange = (
    newDeliveryPreference: Deal['delivery_preference'],
  ) => {
    updateDeliveryPreferenceMutation.mutate({
      delivery_preference: newDeliveryPreference,
    });
  };

  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentOption = useMemo(() => {
    return DELIVERY_PREFERENCE_OPTIONS_MAP.find(
      (option) => option.value === delivery_preference,
    );
  }, [delivery_preference]);

  return (
    <ComboboxPopover
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_DELIVERY_PREFERENCE}</KeyboardKeyIcon>
      }
      placeholder="Set delivery preference..."
      onChange={handleDeliveryPreferenceChange}
      emptyMessage="No delivery preference found"
      currentOption={currentOption}
      options={DELIVERY_PREFERENCE_OPTIONS_MAP}
      side={side}
      align={align}
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = DELIVERY_PREFERENCE_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );
        if (option) {
          handleDeliveryPreferenceChange(option.value);

          // a hack to close the popover
          buttonRef.current?.click();
        }
      }}
    >
      {/* DEV NOTE: the child of a combobox needs to be able to accept a ref thus */}
      <div>
        <DealDeliveryPreferenceBadge
          value={delivery_preference}
          hideCopy={hideCopy}
          hideIcon={hideIcon}
          buttonRef={buttonRef}
          useAbbreviation={useAbbreviation}
          className={className}
        />
        {withHotkeyListener && (
          <HotKeyListener
            hotkeys={HOTKEYS.DEAL_DELIVERY_PREFERENCE}
            onHotkeyPressed={() => {
              buttonRef.current?.click();
            }}
          />
        )}
      </div>
    </ComboboxPopover>
  );
};

export default DealDeliveryPreferenceSelector;
